import React, { useEffect, useRef, useState } from "react";
import { usePersistentState, markNavigationPreserve, consumeNavigationPreserve, clearNamespace } from "../../hooks/persistence";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Tooltip,
  IconButton,
  ClickAwayListener,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GridColDef } from "@mui/x-data-grid";
import "../Dynamic/UserList.scss";
import { CommonService } from "../../Service/CommonService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router";
import Constant from "../../Constant";
import DataGridLoader from "../Common/DataGridLoader";

const ContractPricingListing: React.FC = () => {
  const history = useHistory();
  const [contractPricing, setContractPricing] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);

  const initialFilter = {
    type: null,
    date: null,
    putAway: null,
    barrelStorage: null,
    palletStorage: null,
    ownershipTransfer: null,
    toteStorage: null
  };

  const _filterInitRef = useRef(false);
  if (!_filterInitRef.current) {
    _filterInitRef.current = true;
    if (!consumeNavigationPreserve("contract-pricing")) {
      clearNamespace("contract-pricing");
    }
  }

  const [page, setPage] = usePersistentState<number>("contract-pricing", "page", 0);
  const [pageSize, setPageSize] = usePersistentState<number>("contract-pricing", "pageSize", 50);
  const [sortField, setSortField] = usePersistentState<string>("contract-pricing", "sortField", "createdDate");
  const [sortDirection, setSortDirection] = usePersistentState<string>("contract-pricing", "sortDirection", "desc");
  const [filter, setFilter] = usePersistentState<any>("contract-pricing", "filter", initialFilter);
  

  const handleAddContract = () => {
    history.push("/master/add-contract");
  };

  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
      const newSortField = sortModel[0].field;
      const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

      setSortField(newSortField);
      setSortDirection(newSortDirection);

      fetchAllContractPricing(
        page,
        pageSize,
        newSortField,
        newSortDirection,
        filter
      );
    }
  };

  const fetchAllContractPricing = async (
    page: any,
    size: any,
    sortField: any,
    sortDirection: any,
    filter: any
  ) => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setLoading(true);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      const response = await InventoryApiService.fetchAllContractPricing(
        page,
        size,
        sortField,
        sortDirection,
        filter,
        signal
      );
      setContractPricing(response.data.content);
      setTotalCount(response.data.totalElements);
    } catch (error) {
      console.log("error occurred while api calling " + error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllContractPricing(
      page,
      pageSize,
      sortField,
      sortDirection,
      filter
    );
  }, []);

  const handleBack = () => {
    history.push("/master/dashboard");
  };

  const handleRowClick = (params: any) => {
    const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";
    markNavigationPreserve("contract-pricing");
    history.push(`${linkPrefix}/contract-pricing-details/${params.row.id}`);
  };

  //add tooltip
  const CellWithTooltip = ({ value }: { value: string | number }) => {
    return (
      <Tooltip arrow title={value?.toString()}>
        <span>{value?.toString()}</span>
      </Tooltip>
    );
  };

  const handleEditContract = (id:any) => {
    history.push('/master/edit-contract/' + id);
  }

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof any
  ) => {
    const value = event.target.value;

    // Prepare a temporary filter object with the updated field value
    const tempFilter = {
      ...filter, // Spread existing filter state
      [field]: value, // Update field only if the condition is met
    };

    // Update the filter state
    setFilter(tempFilter);

    //call api
    fetchAllContractPricing(
      page,
      pageSize,
      sortField,
      sortDirection,
      tempFilter
    );
  };

  const columns: GridColDef[] = [
    ...(CommonService.isAdmin()
      ? [
          {
            field: "type",
            headerName: "Pricing Type",
            flex: 1,
            minWidth: 130,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader: (params) => (
              <div>
                <Typography
                  variant="body2"
                  sx={{ color: "#6F6F6F", fontSize: "14px" }}
                >
                  {params.colDef.headerName}
                </Typography>
                <div className="position-relative" style={{ width: "120px" }}>
                  <input
                    type="text"
                    className="form-control border mt-1"
                    style={{ textIndent: "24px", boxShadow: "none" }}
                    placeholder="Search"
                    value={filter.companyName}
                    defaultValue={filter.companyName}
                    onChange={(event) =>
                      handleFilterChange(event, "type")
                    }
                    onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
                  />
                  <SearchIcon
                    sx={{
                      color: "#6F6F6F",
                      position: "absolute",
                      top: "50%",
                      left: "8px",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            ),
            renderCell: (params: any) => (
              <CellWithTooltip value={params.value || "-"} />
            ),
          },
        ]
      : []),
    {
      field: "date",
      headerName: "Date",
      minWidth: 130,
      flex: 1,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "120px" }}>
            <input
              type="date"
              className="form-control border mt-1"
              style={{ boxShadow: "none" }}
              id="birthdate"
              name="modifiedDate"
              placeholder="MM/DD/YYYY"
              value={filter.startDate}
              defaultValue={filter.startDate}
              onChange={(event) => handleFilterChange(event, "date")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip
          value={CommonService.formatDateToReadableString(params.value) || "-"}
        />
      ),
    },
    {
      field: "putAway",
      headerName: "Put Away",
      minWidth: 120,
      flex: 1,
      sortable: false, // disables sorting for this column
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 100 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.contractType}
              defaultValue={filter.contractType}
              onChange={(event) => handleFilterChange(event, "putAway")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "barrelStorage",
      headerName: "Container Storage",
      minWidth: 120,
      flex: 1,
      sortable: false, // disables sorting for this column
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 100 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.contractType}
              defaultValue={filter.contractType}
              onChange={(event) => handleFilterChange(event, "barrelStorage")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "palletStorage",
      headerName: "Pallet Storage",
      minWidth: 120,
      flex: 1,
      sortable: false, // disables sorting for this column
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 100 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.contractType}
              defaultValue={filter.contractType}
              onChange={(event) => handleFilterChange(event, "palletStorage")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "toteStorage",
      headerName: "Tote Storage",
      minWidth: 120,
      flex: 1,
      sortable: false, // disables sorting for this column
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 100 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.contractType}
              defaultValue={filter.contractType}
              onChange={(event) => handleFilterChange(event, "toteStorage")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "ownershipTransfer",
      headerName: "Ownership Transfer",
      minWidth: 120,
      flex: 1,
      sortable: false, // disables sorting for this column
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 100 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.contractType}
              defaultValue={filter.contractType}
              onChange={(event) => handleFilterChange(event, "ownershipTransfer")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },

    // we don't need action column as of now in this table (jun 17, 2025)
    // ...(CommonService.isAdmin()
    //   ? [
    //       {
    //         field: "action",
    //         headerName: "Action",
    //         flex: 1,
    //         minWidth: 100,
    //         sortable: false,
    //         renderCell: (params: any) => (
    //           <>
    //             {(CommonService.isAdmin() && params.row.status == Constant.ContractStatus.ACTIVE) && (
    //                 <div
    //                   className="d-flex align-items-center gap-2 h-100"
    //                   onClick={(e) => e.stopPropagation()}
    //                 >
    //                   {/* <Tooltip title="Amendment contract" arrow>
    //                     <IconButton
    //                       aria-label="message"
    //                       size="small"
    //                       onClick={() => handleEditContract(params?.row?.id)}
    //                       disabled={
    //                         params?.row?.roleName == "SUPER_ADMIN" &&
    //                         CommonService.isUser()
    //                       }
    //                     >
    //                       <img
    //                         src={EditIcon}
    //                         className="cursor-pointer"
    //                         alt="EditIcon"
    //                       />
    //                     </IconButton>
    //                   </Tooltip> */}
    //                 </div>
    //               )}
    //           </>
    //         ),
    //       },
    //     ]
    //   : []),
  ];

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    fetchAllContractPricing(
      newPaginationModel.page,
      newPaginationModel.pageSize,
      sortDirection,
      sortField,
      filter
    );
  };

  return (
    <div className="usersList pb-5">
      {CommonService.isAdmin() && (
        <>
          <div className="usersList__header--wrapper d-flex flex-wrap justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <IconButton
                onClick={handleBack}
                aria-label="ArrowBackIcon"
                sx={{
                  backgroundColor: "#FFF",
                  mb: 1,
                  "&:hover": { backgroundColor: "#FFF" },
                }}
              >
                <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
              </IconButton>
              <div className="header--title mb-2 me-2">
                <h1 className="title mb-0 ms-2"> Contract Pricing</h1>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="user-table-container">
        <div className="userTable">
          <DataGrid
            rows={contractPricing}
            columns={columns}
            className="data-grid support-req--listing"
            initialState={{
              pagination: {
                paginationModel: { page, pageSize },
              },
            }}
            pagination
            rowCount={totalCount}
            paginationModel={{ page, pageSize }}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortChange}
            onPaginationModelChange={onPaginationModelChange}
            disableRowSelectionOnClick={true}
            pageSizeOptions={[10, 20, 50, 100]}
            loading={loading}
            slots={{
              loadingOverlay: DataGridLoader,
            }}
            disableColumnResize
            onRowClick={handleRowClick}
          />
        </div>
      </div>
    </div>
  );
};

export default ContractPricingListing;
